/** Turns one MediaStream (the microphone, or the shared call sound) into timed chunks (decision 0051).
 *
 * The stream goes through WebAudio first (MediaStreamAudioSourceNode -> MediaStreamAudioDestinationNode) for two reasons:
 * the destination's stream has no video track by construction, and it produces continuous samples (silence when the
 * source is silent), so both tracks keep the same length and the same clock. The graph is **never connected to the
 * speakers**: nothing is played back. A level meter reads an analyser on the source. */
import { levelFromBytes } from "../device-check-core.js";
import type { Track } from "./types.js";

export interface Chunk {
  track: Track;
  seq: number;
  data: Blob;
  started_ms: number; // from the recording's start, on the shared clock
  duration_ms: number;
  content_type: string;
}

export interface TrackCapture {
  readonly track: Track;
  start(t0: number): void; // t0: the shared clock's zero (performance.now at the recording's start)
  stop(): Promise<void>; // flushes the last chunk
  level(): number; // 0..1
  hasVideoTrack(): boolean;
  onEnded(cb: () => void): void;
  chunkCount(): number;
  /** Swaps the sound source mid-recording (the accountant shares again after sharing stopped). The recorder keeps
   * running on the same destination, so the track stays continuous and nothing restarts. */
  replaceStream(stream: MediaStream): void;
}

export interface CaptureOptions {
  track: Track;
  stream: MediaStream;
  ctx: AudioContext;
  mimeType: string;
  chunkMs: number;
  onChunk(chunk: Chunk): void;
  now(): number;
  /** CAP-009 Pause/Continue: a capture made after a pause numbers its chunks on from the recording's earlier ones. */
  firstSeq?: number;
}

/** Removes any video track from a share (stopping it), and says whether one is left. */
export function stripVideo(stream: MediaStream): boolean {
  for (const t of stream.getVideoTracks()) {
    t.stop();
    stream.removeTrack(t);
  }
  return stream.getVideoTracks().length > 0;
}

export function createTrackCapture(o: CaptureOptions): TrackCapture {
  if (stripVideo(o.stream)) throw new Error("A video track is still on the stream.");
  let currentStream = o.stream;
  let source = o.ctx.createMediaStreamSource(currentStream);
  const analyser = o.ctx.createAnalyser();
  analyser.fftSize = 1024;
  const dest = o.ctx.createMediaStreamDestination();
  source.connect(analyser);
  source.connect(dest); // NOT o.ctx.destination: no playback, no feedback
  const bytes = new Uint8Array(analyser.fftSize);
  // The recorder only ever sees the destination's audio tracks.
  const recorderStream = new MediaStream(dest.stream.getAudioTracks());
  const recorder = new MediaRecorder(recorderStream, { mimeType: o.mimeType, audioBitsPerSecond: 32_000 });
  const contentType = o.mimeType.split(";")[0]!.trim();
  let t0 = 0;
  let lastAt = 0;
  let seq = o.firstSeq ?? 0;
  const ended: Array<() => void> = [];
  recorder.ondataavailable = (ev) => {
    if (ev.data.size === 0) return;
    const at = o.now() - t0;
    o.onChunk({ track: o.track, seq: seq++, data: ev.data, started_ms: Math.round(lastAt), duration_ms: Math.max(0, Math.round(at - lastAt)), content_type: contentType });
    lastAt = at;
  };
  const watch = (stream: MediaStream) => {
    for (const t of stream.getAudioTracks()) t.addEventListener("ended", () => stream === currentStream && ended.forEach((cb) => cb()));
  };
  watch(currentStream);
  return {
    replaceStream(stream: MediaStream) {
      if (stripVideo(stream)) throw new Error("A video track is still on the stream.");
      source.disconnect();
      for (const t of currentStream.getTracks()) t.stop();
      currentStream = stream;
      source = o.ctx.createMediaStreamSource(currentStream);
      source.connect(analyser);
      source.connect(dest); // still not the speakers
      watch(currentStream);
    },
    track: o.track,
    start(zero: number) {
      t0 = zero;
      // On the recording's own clock: 0 at the start; after a pause, where the recording's wall clock now is.
      lastAt = Math.max(0, o.now() - zero);
      recorder.start(o.chunkMs);
    },
    stop() {
      return new Promise<void>((resolve) => {
        if (recorder.state === "inactive") return resolve();
        recorder.addEventListener("stop", () => resolve(), { once: true });
        recorder.stop();
      }).then(() => {
        for (const t of currentStream.getTracks()) t.stop();
        source.disconnect();
      });
    },
    level() {
      analyser.getByteTimeDomainData(bytes);
      return levelFromBytes(bytes);
    },
    hasVideoTrack: () => recorderStream.getVideoTracks().length > 0,
    onEnded: (cb) => ended.push(cb),
    chunkCount: () => seq,
  };
}
